import { Container, Sprite, Point, Texture } from 'pixi.js';
import { app } from "../app";
import { Actor } from './Actor';
import { Pathfinder } from './../pathfinding/Pathfinder';
import { AStar, heuristics } from './../pathfinding/AStar';

export class Player extends Container {
    static direction = {
        up: 'UP',
        down: 'DOWN',
        left: 'LEFT',
        right: 'RIGHT'
    };

    constructor(game, actors, missionData) {
        super();

        this.isLocked = true;

        this.game = game;
        this.tilemap = game.tilemap;
        //this.map = this.tilemap.map;
        this.collisionGraph = this.tilemap.collisionGraph;
        this.movementData = this.tilemap.map.movementData;
        this.missionData = missionData;

        this.pathfinder = new Pathfinder(this.collisionGraph, AStar, { heuristic: heuristics.diagonal });

        this.actors = new Map();
        this.initActors(actors);

        this.movements = new Map();
        this.buildMovementTable(this.movements, this.actors);

        if (missionData.loadout.switchShield) {
            this.switchShield = new Sprite.from(missionData.loadout.switchShield || Texture.WHITE);
            this.switchShield.anchor.set(0.5);
            this.switchShield.position.copyFrom(this.tilemap.tileOffset);
            this.switchShield.visible = false;
            this.addChild(this.switchShield);
        }

        // Position
        this.tileX = this.tilemap.spawnTile.x;
        this.tileY = this.tilemap.spawnTile.y;
        this.tilePos = new Point(this.tileX, this.tileY);
        this.position.copyFrom(this.tilemap.localToWorldXY(this.tileX, this.tileY));
        //this.tilemap.position.set(-this.x, -this.y);

        // Movement
        this.moveDelta = { x: 0, y: 0 };
        this.moveDirection = Player.direction.down;
        this.moveDistance = 64;
        this._baseMoveTime = 250;
        this.shieldTime = 300;
        //this.moveTime = this.baseMoveTime;
        this.moveType = 1;
        this.hasDirectionChanged = false;
        this.hasActorChanged = false;

        // Movement State
        this.inputBuffer = [];
        this.isMoving = false;
        this.autoMoving = false;

        this.getInitActor(this.tileX, this.tileY);

        // Stack of effects applied to the player.
        this.effects = [];
        // Cumulative effect to apply.
        this.cmlEffectsDefaults = {
            baseMoveTime: 1,
            speed: 1,
        };
        this.cmlEffects = Object.assign({}, this.cmlEffectsDefaults);
    }

    set tilePosition(newTilePos) {
        this.tilePos = newTilePos;
        this.tileX = this.tilePos.x;
        this.tileY = this.tilePos.y;
        let worldPos = this.tilemap.localToWorldXY(newTilePos.x, newTilePos.y);
        this.position.copyFrom(worldPos);
    }

    get baseMoveTime() {
        return this._baseMoveTime * this.cmlEffects.baseMoveTime;
    }

    get speed() {
        return this.currActor.speed * this.cmlEffects.speed;
    }

    get moveTime() {
        return this.baseMoveTime / this.speed;
    }

    initActors(actors) {
        Object.keys(actors).forEach((key) => {
            const data = actors[key];

            if (!data || !data.turnarounds)
                return;

            let actor = new Actor(key, data, this.tilemap.tileOffset);
            actor.visible = false;
            this.actors.set(key, actor);
            this.addChild(actor);
        });
    }

    buildMovementTable(movements, actors) {
        actors.forEach(actor => {
            actor.movementMap.forEach((value, key) => {
                if (!movements.has(key) || value > movements.get(key).value) {
                    movements.set(key, { actor: actor, value: value });
                }
            });
        });
    }

    getInitActor(tileX, tileY) {
        let allowedTypes = this.movementData.getMoveTypes(this.tilemap.localXYToIndex(tileX, tileY));

        if (allowedTypes.length > 0 && this.movements.has(allowedTypes[0])) {
            this.currActor = this.movements.get(allowedTypes[0]).actor;
            this.moveType = allowedTypes[0];
        } else {
            this.currActor = this.actors.get('avatar');
            this.moveType = 1;

        }

        //this.moveTime = this.baseMoveTime / this.currActor.speed;
        this.currActor.visible = true;
    }

    applyEffect(effect) {
        effect.time = effect.totalTime;
        this.effects.push(effect);
    }

    /**
     * Main update loop and is called every update. Is used for things that
     * always need to update without condition.
     *
     * @param {float} dt
     */
    update(dt) {
        let cmlEffects = Object.assign({}, this.cmlEffectsDefaults);
        for (let i = this.effects.length - 1; i >= 0; i--) {
            const effect = this.effects[i];
            effect.time -= dt;

            if (effect.time <= 0) {
                // Effect has run it's course. Time for it to go.
                this.effects.splice(i, 1);
                continue;
            }
            effect.applyEffect(cmlEffects);
        }
        this.cmlEffects = cmlEffects;
    }

    /**
     * Is updated whenever a key change is detected in the controller.
     * It updates the player to move in the direction of the new delta.
     *
     * @param {Point} newDelta
     */
    updateMovement(newDelta) {
        if (this.isLocked) return;

        this.moveDelta = newDelta;
        this.moveDirection = this.calculateDirection(this.moveDelta);
        this.autoMoving = false;
        this.inputBuffer = [];

        if (this.isMoving || (this.moveDelta.x === 0 && this.moveDelta.y === 0)) return;

        this.tryMove(this.moveDelta);
    }

    /**
     * Is run every update if no key change has been detected.
     */
    tryAutoMove() {
        if (this.isLocked || this.autoMoving || this.inputBuffer.length <= 0) return;

        this.autoMoving = true;

        let posDelta = this.inputBuffer.shift();
        this.moveDirection = this.calculateDirection(posDelta);
        this.tryMove(posDelta);
    }

    moveTo(targetX, targetY) {
        if (this.moveDelta.x !== 0 && this.moveDelta.y !== 0) return;
        let path = this.pathfinder.pathTo(
            this.tilemap.worldToLocalXY(this.x, this.y),
            this.tilemap.worldToLocalXY(targetX, targetY));
        this.inputBuffer = path;
    }

    calculateDirection(moveDelta) {
        let direction = this.moveDirection;

        if (moveDelta.y < 0) direction = Player.direction.up;
        else if (moveDelta.y > 0) direction = Player.direction.down;
        else if (moveDelta.x < 0) direction = Player.direction.left;
        else if (moveDelta.x > 0) direction = Player.direction.right;

        if (direction !== this.moveDirection) this.hasDirectionChanged = true;
        return direction;
    }

    tryMove(posDelta) {
        if (this.preMove(posDelta)) this.move(posDelta);
        else if (this.isMoving) this.stopMoving();
    }

    preMove(posDelta) {
        let targetPos = new Point(this.tileX + posDelta.x, this.tileY + posDelta.y);
        // Check if in map bounds.
        if (targetPos.x < 0 || targetPos.x >= this.tilemap.width ||
            targetPos.y < 0 || targetPos.y >= this.tilemap.height) {
            this.autoMoving = false;
            return false;
        }

        // Check if new tile is allowed. // Check the tile is free.
        let allowedTypes = this.movementData.getMoveTypes(this.tilemap.localXYToIndex(targetPos.x, targetPos.y));
        if (allowedTypes.length <= 0 || !this.movements.has(allowedTypes[0]) || this.checkCollisions(targetPos)) {
            this.autoMoving = false;
            return false;
        }

        /*         let allowedTypes = this.movementData.getMoveTypes(this.tilemap.localXYToIndex(targetPos.x, targetPos.y));
                if (allowedTypes.length <= 0 || !this.movements.has(allowedTypes[0]) || this.collisionGraph.checkForEntity(targetPos)) {
                    if (this.collisionGraph.checkForEntity(targetPos)) this.game.missionRunner.onInteract({ interactPos: targetPos });
                    this.autoMoving = false;
                    return false;
                } */

        let nextType = allowedTypes[0];
        if (nextType !== this.moveType) {
            this.nextActor = this.movements.get(nextType).actor;
            this.nextType = nextType;
            this.hasActorChanged = true;
        }

        if (!this.isMoving || this.hasDirectionChanged) {
            this.currActor.setDirection(this.moveDirection);
            this.currActor.move();
            this.hasDirectionChanged = false;
        }

        if (this.collisionGraph.checkForTeleporter(this, targetPos)){
            return false;
        }

        this.collisionGraph.checkForObstacle(this, targetPos);
        this.collisionGraph.checkForCollectible(targetPos);
        return true;
    }

    checkCollisions(targetPos) {
        let moveBlocked = false,
            minX = targetPos.x - (this.currActor.range - 1),
            maxX = targetPos.x + (this.currActor.range - 1),
            minY = targetPos.y - (this.currActor.range - 1),
            maxY = targetPos.y + (this.currActor.range - 1);

        for (let i = minX; i <= maxX; i++)
            for (let j = minY; j <= maxY; j++) {
                const point = new Point(i, j);

                if (this.collisionGraph.checkForEntity(point)) {
                    this.game.missionRunner.onInteract({ interactPos: point });
                    moveBlocked = true;
                    console.log("Move blocked by " + point.x + ", " + point.y + " from " + targetPos.x + ", " + targetPos.y);
                }
            }

        return moveBlocked;
    }

    move(posDelta) {
        this.isMoving = true;

        this.tileX += posDelta.x;
        this.tileY += posDelta.y;
        this.tilePos = new Point(this.tileX, this.tileY);
        let moveX = posDelta.x * this.moveDistance;
        let moveY = posDelta.y * this.moveDistance;
        let moveTime = this.moveTime;

        if (this.hasActorChanged) {
            // Hide current actor.
            this.currActor.visible = false;
            moveTime = this.shieldTime;

            // Show switch shield.
            this.switchShield.scale.set(0);
            this.switchShield.visible = true;

            app.tween(this.switchShield.scale)
                .to({ x: 1, y: 1 }, 100, createjs.Ease.linear)
                .call(() => {
                    this.currActor = this.nextActor;
                    this.currActor.setDirection(this.moveDirection);
                    this.currActor.move();
                    this.moveType = this.nextType;
                    this.currActor.visible = true;
                    moveTime = this.moveTime;
                    app.sound.play(this.currActor.data.switchSound);
                })
                .wait(150)
                .to({ x: 0, y: 0 }, 50, createjs.Ease.circIn);

            this.hasActorChanged = false;
        }

        app.tween(this)
            .to({ x: this.x + moveX, y: this.y + moveY }, moveTime, createjs.Ease.linear)
            .call(() => this.shouldMoveAgain());
    }

    shouldMoveAgain() {
        if (this.autoMoving) {
            if (this.inputBuffer.length <= 0) this.stopMoving();
            else {
                let posDelta = this.inputBuffer.shift();
                this.moveDirection = this.calculateDirection(posDelta);
                this.tryMove(posDelta);
            }
        } else if (this.moveDelta.x !== 0 || this.moveDelta.y !== 0) {
            this.tryMove(this.moveDelta);
        } else {
            this.stopMoving();
        }
    }

    stopMoving() {
        this.isMoving = false;
        this.autoMoving = false;
        this.currActor.idle();
    }

    lock(isLocked) {
        this.isLocked = isLocked;
    }

    celebrate() {
        if (this.currActor !== this.actors.get('avatar')) {
            this.currActor.visible = false;
            this.currActor = this.actors.get('avatar');
            this.currActor.visible = true;
        }

        this.currActor.celebrate();
    }
}
